import React, { useEffect, useState, useCallback } from "react";
import { capitalize, debounce } from "lodash";
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  CardHeader, 
  IconButton, 
  Input, 
  InputAdornment, 
  Tooltip, 
  Dialog, 
  DialogActions, 
  DialogTitle,
  Grid,
  Typography,
  Chip,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Stack,
  Paper,
  Container,
  Fade,
  FormControlLabel,
  FormGroup,
  Switch,
  Pagination,
  Select,
  MenuItem,
  FormControl
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { apiJackpotService } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { useNavigate } from "react-router-dom";
import { Loader } from "../../components/CustomLoader";
import { fDate, isSameLocalDate, isSameUTCDate } from "../../utils/formatTime";
import { Helmet } from "react-helmet-async";
import { TrimText } from "../../utils/helpers";
import { EditOutlined } from "@mui/icons-material";
import { isToday } from 'date-fns';
import TableFilter from "../../components/shared/TableFilter";
import { STATUS_OPTIONS_LIST } from "../../utils/constants";

export default function JackpotManagementList() {
  const navigate = useNavigate();
  const [searchItem, setSearchItem] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState([]);
  const [show, setShow] = useState(false);
  const [warning, setWarning] = useState("");
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [totalActiveJackpots, setTotalActiveJackpots] = useState(0);
  const [totalInactiveJackpots, setTotalInactiveJackpots] = useState(0);
  const [totalOngoingJackpots, setTotalOngoingJackpots] = useState(0);
  const [totalAnnouncedJackpots, setTotalAnnouncedJackpots] = useState(0);

  const fetchUsers = async (query) => {
    const body = {
      searchItem: query || "",
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status,
    };
    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const res = await apiJackpotService.getAll(body);
      setTimeout(() => {
        if (res.status === 200) {
          setData(res?.data?.data?.result);
          setTotalPages(res?.data?.data?.totalRecords);
          
          // Calculate stats
          if (res?.data?.data?.result) {
            const jackpots = res.data.data.result;
            setTotalActiveJackpots(jackpots.filter(item => item.status === "active").length);
            setTotalInactiveJackpots(jackpots.filter(item => item.status === "inactive").length);
            setTotalOngoingJackpots(jackpots.filter(item => item.status === "ongoing").length);
            setTotalAnnouncedJackpots(jackpots.filter(item => item.status === "announced").length);
          }
        }
        setLoaderDelay(true);
      }, 500);
    } catch (err) {
      setData(err?.response?.data?.message?.data);
      errorHandler(err);
      setLoaderDelay(false);
    } finally {
      setIsLoading(false);
      setLoaderDelay(false);
    }
  };

  const debouncedSearchFunction = useCallback(
    debounce((query) => {
      setDebouncedSearch(query);
    }, 400),
    []
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    setCurrentPage(1);
    debouncedSearchFunction(query);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const deleteData = async (id) => {
    Swal.fire({
      text: "Are you sure you want to delete this Jackpot?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await apiJackpotService.delete({ id });
          if (res?.status === 200) {
            toast.success(res?.data?.message, { toastId: "success" });
            setData((prevData) => prevData.filter((item) => item._id !== id));
            setTotalPages((prevTotal) => prevTotal - 1);
            setRefresh(!refresh);
          } else {
            toast.error(res?.data?.message, { toastId: "error" });
          }
        } catch (error) {
          errorHandler(error);
        }
      }
    });
  };

  const toggleChecked = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const res = await apiJackpotService.changeStatus({
        id,
        status: newStatus,
      });

      if (res.status === 200) {
        toast.success(res?.data?.message, { toastId: "success" });
        setRefresh(!refresh);
      } else {
        toast.error(res?.data?.message, { toastId: "error" });
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setDebouncedSearch(searchItem); 
    }, 500); 

    return () => clearTimeout(delayDebounceFn); 
  }, [searchItem]);


  useEffect(() => {
    const body = {
      searchItem: debouncedSearch, 
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status,
    };
    setIsLoading(true)
    setLoaderDelay(false); 
    apiJackpotService.getAll(body)
      .then((res) => {
        if (res.status === 200) {
          setTimeout(() => {
            setData(res?.data?.data?.result);
            setTotalPages(res?.data?.data?.totalRecords);
            
            // Calculate stats
            if (res?.data?.data?.result) {
              const jackpots = res.data.data.result;
              setTotalActiveJackpots(jackpots.filter(item => item.status === "active").length);
              setTotalInactiveJackpots(jackpots.filter(item => item.status === "inactive").length);
              setTotalOngoingJackpots(jackpots.filter(item => item.status === "ongoing").length);
              setTotalAnnouncedJackpots(jackpots.filter(item => item.status === "announced").length);
            }
            
            setLoaderDelay(true); 
          }, 500);
        }
      })
      .catch((err) => {
        setIsLoading(false)
        errorHandler(err);
        setLoaderDelay(true); 
      }) 
      .finally(() => {
        setIsLoading(false);
        setLoaderDelay(false);
      });
  }, [debouncedSearch, currentPage, pageSize, refresh, status]);

  const renderJackpotCard = (jackpot, index) => (
    <Grid item xs={12} sm={6} md={4} lg={3} key={jackpot._id}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.05) 100%)',
          border: '1px solid',
          borderColor: 'divider',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          '&:hover': {
            borderColor: 'primary.main',
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 40px rgba(0, 161, 221, 0.2)',
          }
        }}
      >
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
              boxShadow: '0 4px 16px rgba(0, 161, 221, 0.3)'
            }}
          >
            <EmojiEventsIcon sx={{ fontSize: 24, color: 'white' }} />
          </Avatar>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip
              label={capitalize(jackpot.status)}
              size="small"
              sx={{
                backgroundColor: jackpot.status === "active" 
                  ? 'rgba(76, 175, 80, 0.2)' 
                  : jackpot.status === "ongoing"
                  ? 'rgba(255, 152, 0, 0.2)'
                  : jackpot.status === "announced"
                  ? 'rgba(33, 150, 243, 0.2)'
                  : 'rgba(244, 67, 54, 0.2)',
                color: jackpot.status === "active" 
                  ? '#4caf50' 
                  : jackpot.status === "ongoing"
                  ? '#ff9800'
                  : jackpot.status === "announced"
                  ? '#2196f3'
                  : '#f44336',
                fontWeight: 600,
                border: '1px solid',
                borderColor: jackpot.status === "active" 
                  ? 'rgba(76, 175, 80, 0.3)' 
                  : jackpot.status === "ongoing"
                  ? 'rgba(255, 152, 0, 0.3)'
                  : jackpot.status === "announced"
                  ? 'rgba(33, 150, 243, 0.3)'
                  : 'rgba(244, 67, 54, 0.3)'
              }}
            />
          </Stack>
        </Stack>

        {/* Content */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
            {TrimText(jackpot.name, 20)}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            Jackpot #{jackpot.jackpotNumber}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            Draw Date: {fDate(jackpot.drawDate)}
          </Typography>
        </Box>

        {/* Winners Button */}
        {(jackpot.status === "ongoing" || jackpot.status === "announced") && (
          <Box sx={{ mb: 2 }}>
            <Button
              fullWidth
              variant="contained"
              size="small"
              disabled={jackpot.status === "announced"}
              onClick={() =>
                navigate(`/jackpot-management/winners-view/${jackpot._id}`, {
                  state: {
                    contest: jackpot?.contests,
                  },
                })
              }
              sx={{
                borderRadius: 2,
                backgroundColor: jackpot.status === "announced" ? 'rgba(33, 150, 243, 0.3)' : 'black',
                border: '2px solid #00a1dd',
                '&:hover': {
                  backgroundColor: jackpot.status === "announced" ? 'rgba(33, 150, 243, 0.3)' : '#00a1dd',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(33, 150, 243, 0.3)',
                  color: 'white'
                }
              }}
            >
              {jackpot.status === "announced" ? "Announced" : "Announce Winners"}
            </Button>
          </Box>
        )}

        {/* Actions */}
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => navigate(`/jackpot-management/view/${jackpot._id}`)}
            sx={{
              borderRadius: 2,
              borderColor: 'divider',
              color: 'text.secondary',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
              }
            }}
          >
            View
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<EditOutlined />}
            onClick={() => {
              if (jackpot.status !== "announced") {
                navigate(`/jackpot-management/edit/${jackpot._id}`);
              }
            }}
            disabled={jackpot.status === "announced"}
            sx={{
              borderRadius: 2,
              background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0091c8 0%, #3b8fd9 100%)',
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)'
              }
            }}
          >
            Edit
          </Button>
        </Stack>
      </Paper>
    </Grid>
  );

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: '100px'
    },
    {
      name: "Name",
      sortable: true,
      selector: (row) => TrimText(row.name, 25),
    },
    {
      name: "Jackpot Number",
      sortable: true,
      selector: (row) => row.jackpotNumber,
    },
    {
      name: "Draw Date",
      sortable: true,
      selector: (row) => fDate(row.drawDate),
    },
    {
      name: "Status",
      cell: (row) => (
        <Chip
          label={capitalize(row.status)}
          size="small"
          sx={{
            backgroundColor: row.status === "active" 
              ? 'rgba(76, 175, 80, 0.2)' 
              : row.status === "ongoing"
              ? 'rgba(255, 152, 0, 0.2)'
              : row.status === "announced"
              ? 'rgba(33, 150, 243, 0.2)'
              : 'rgba(244, 67, 54, 0.2)',
            color: row.status === "active" 
              ? '#4caf50' 
              : row.status === "ongoing"
              ? '#ff9800'
              : row.status === "announced"
              ? '#2196f3'
              : '#f44336',
            fontWeight: 600,
            border: '1px solid',
            borderColor: row.status === "active" 
              ? 'rgba(76, 175, 80, 0.3)' 
              : row.status === "ongoing"
              ? 'rgba(255, 152, 0, 0.3)'
              : row.status === "announced"
              ? 'rgba(33, 150, 243, 0.3)'
              : 'rgba(244, 67, 54, 0.3)'
          }}
        />
      ),
    },
    {
      name: "Winners",
      cell: (row) => {
        const canAnnounce = row.status === "ongoing" || row.status === "announced";

        return canAnnounce ? (
          <Tooltip>
            <span>
              <Button
                variant="contained"
                size="small"
                color="primary"
                disabled={row.status === "announced"}
                onClick={() =>
                  navigate(`/jackpot-management/winners-view/${row._id}`, {
                    state: {
                      contest: row?.contests,
                    },
                  })
                }
                sx={{
                  width: '100px',
                  backgroundColor: 'black',
                  border: '2px solid #00a1dd',
                  '&:hover': {
                    backgroundColor: '#00a1dd',
                  },
                }}
              >
                {row.status === "announced" ? "Announced" : "Announce"}
              </Button>
            </span>
          </Tooltip>
        ) : (
          "-"
        );
      },
    },
    {
      name: "Action",
      center: true,
      cell: (row) => (
        <>
          <Tooltip title={row.status === "announced" ? "Editing Disabled" : "Edit"} placement="left">
            <span>
              <IconButton
                color="primary"
                onClick={() => {
                  if (row.status !== "announced") {
                    navigate(`/jackpot-management/edit/${row?._id}`);
                  }
                }}
                disabled={row.status === "announced"}
              >
                <EditOutlined />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="View" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/jackpot-management/view/${row?._id}`);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete" placement="top">
            <IconButton color="primary" onClick={() => deleteData(row._id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Helmet>
        <title>Jackpot Management</title>
      </Helmet>
      <Fade in timeout={500}>
        <Stack spacing={4}>
          {/* Header Section */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
              border: '1px solid',
              borderColor: 'divider',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #00a1dd 0%, #42a5f5 50%, #90caf9 100%)',
              }
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={3}>
                <Avatar
        sx={{
                    width: 64,
                    height: 64,
                    background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                    boxShadow: '0 8px 32px rgba(0, 161, 221, 0.4)'
                  }}
                >
                  <EmojiEventsIcon sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                    Jackpot Management
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Manage and monitor all jackpots in your system
                  </Typography>
                </Box>
              </Stack>
              
              <Button
                size="large"
                variant="contained"
                onClick={() => navigate("/jackpot-management/add")}
                sx={{
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                  boxShadow: '0 8px 32px rgba(0, 161, 221, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0091c8 0%, #3b8fd9 100%)',
                    boxShadow: '0 12px 40px rgba(0, 161, 221, 0.4)',
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                + Add Jackpot
              </Button>
            </Stack>

            {/* Stats Cards */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'rgba(0, 161, 221, 0.1)',
                    border: '1px solid rgba(0, 161, 221, 0.2)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 1 }}>
                    {data?.length || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Total Jackpots
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'rgba(76, 175, 80, 0.1)',
                    border: '1px solid rgba(76, 175, 80, 0.2)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 700, mb: 1 }}>
                    {totalActiveJackpots}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Active Jackpots
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'rgba(255, 152, 0, 0.1)',
                    border: '1px solid rgba(255, 152, 0, 0.2)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: '#ff9800', fontWeight: 700, mb: 1 }}>
                    {totalOngoingJackpots}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Ongoing Jackpots
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'rgba(33, 150, 243, 0.1)',
                    border: '1px solid rgba(33, 150, 243, 0.2)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: '#2196f3', fontWeight: 700, mb: 1 }}>
                    {totalAnnouncedJackpots}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Announced Jackpots
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          {/* Controls Section */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', gap: 2 }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
                <TableFilter
                  data={STATUS_OPTIONS_LIST}
                  value={status}
                  setValue={setStatus}
                  filterName="Status"
                />
        <Input
          value={searchItem}
                  placeholder="Search jackpots..."
          onChange={handleSearchChange}
                  sx={{
                    color: 'white',
                    '& .MuiInput-input': {
                      color: 'white',
                    },
                    '& .MuiInput-input::placeholder': {
                      color: 'rgba(255, 255, 255, 0.5)',
                      opacity: 1,
                    },
                    '& .MuiInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 2,
                      padding: '8px 16px',
                    }
                  }}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="clear-search"
                onClick={() => {
                  const cleared = "";
                  setSearchItem(cleared);
                }}
                        sx={{ color: 'text.secondary' }}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />
              </Stack>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => newMode && setViewMode(newMode)}
                sx={{
                  '& .MuiToggleButton-root': {
                    color: 'text.secondary',
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="grid">
                  <ViewModuleIcon />
                </ToggleButton>
                <ToggleButton value="list">
                  <ViewListIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Paper>

          {/* Content Section */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}
          >
            {viewMode === 'grid' ? (
              /* Grid View */
              <Box sx={{ p: 3 }}>
                {isLoading || !loaderDelay ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <Loader />
                  </Box>
                ) : !data || data.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 3,
                        background: 'linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)',
                        boxShadow: '0 8px 32px rgba(156, 39, 176, 0.3)'
                      }}
                    >
                      <EmojiEventsIcon sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                      No jackpots found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your search criteria or create a new jackpot
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Grid container spacing={3}>
                      {data.map((jackpot, index) => renderJackpotCard(jackpot, index))}
                    </Grid>
                    
                    {/* Pagination for Grid View */}
                    <Box sx={{ mt: 4 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', gap: 2 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalPages)} of {totalPages} jackpots
                        </Typography>
                        
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <FormControl size="small" sx={{ minWidth: 80 }}>
                            <Select
                              value={pageSize}
                              onChange={(e) => {
                                setPageSize(e.target.value);
                                setCurrentPage(1);
                              }}
                              sx={{
                                color: 'text.secondary',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'divider',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'primary.main',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'primary.main',
                                }
                              }}
                            >
                              <MenuItem value={10}>10</MenuItem>
                              <MenuItem value={20}>20</MenuItem>
                              <MenuItem value={50}>50</MenuItem>
                            </Select>
                          </FormControl>
                          
                          <Pagination
                            count={Math.ceil(totalPages / pageSize)}
                            page={currentPage}
                            onChange={(event, page) => handlePageChange(page)}
                            color="primary"
                            size="large"
                            sx={{
                              '& .MuiPaginationItem-root': {
                                color: 'text.secondary',
                                border: '1px solid',
                                borderColor: 'divider',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 161, 221, 0.1)',
                                  borderColor: 'primary.main',
                                },
                                '&.Mui-selected': {
                                  backgroundColor: 'primary.main',
                                  color: 'white',
                                  '&:hover': {
                                    backgroundColor: 'primary.dark',
                                  }
                                }
                              }
                            }}
                          />
                        </Stack>
                      </Stack>
                    </Box>
                  </>
                )}
      </Box>
            ) : (
              /* List View */
              <Box>
        <DataTable
          key={`datatable-${currentPage}-${pageSize}-${viewMode}`}
          customStyles={{
            headCells: {
              style: {
                fontSize: "15px",
                fontWeight: "bold",
                        backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        color: 'black'
                      },
                    },
                    rows: {
                      style: {
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        }
                      }
                    }
          }}
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalPages}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          paginationPerPage={pageSize}
          paginationRowsPerPageOptions={[10, 20, 50]}
                  onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
                    setPageSize(currentRowsPerPage);
                    setCurrentPage(currentPage);
                  }}
          progressPending={isLoading || !loaderDelay}
          progressComponent={<Loader />}
          className="custom"
        />
              </Box>
            )}
          </Paper>
        </Stack>
      </Fade>

      {/* Status Change Dialog */}
      <Dialog open={show} onClose={() => setShow(false)} className="popupbox-statis">
        <DialogTitle>{warning}</DialogTitle>
        <DialogActions className="status-box-action">
          <Button
            variant="outlined"
            onClick={() => {
              toggleChecked(statusId, currentStatus);
              setShow(false);
            }}
            color="primary"
          >
            Yes
          </Button>
          <Button variant="outlined" onClick={() => setShow(false)} color="primary">
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
